import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import { useAuth } from '../../context/AuthContext';
import { usePuoScrivere, puoAccedere } from '../../utils/permessi';
import FormAnagrafica from './FormAnagrafica';
import { TIPI_SOGGETTO, badge, quando } from './comuni';
import useVersioniViste from '../../hooks/useVersioniViste';
import ModificataDaAltri from '../../components/ModificataDaAltri';
import { urlEliminazione, eConflitto, avvisaConflitto } from '../../utils/eliminazione';

const ETICHETTE_CAMPI = {
  ragione_sociale: 'Ragione sociale', codice_fiscale: 'Codice fiscale', partita_iva: 'Partita IVA', tipo_soggetto: 'Tipo di soggetto',
  codice_ateco: 'Codice ATECO', codice_azienda: 'Codice azienda', indirizzo: 'Indirizzo', cap: 'CAP', comune: 'Comune', provincia: 'Provincia',
  pec: 'PEC', email: 'Email', telefono: 'Telefono', note: 'Note', attiva: 'Archiviazione',
};
const AZIONI = { crea: 'Creata', modifica: 'Modificata', elimina: 'Eliminata', ripristina: 'Ripristinata', import: 'Importata' };
const ENTITA = { anagrafica_aziende: 'azienda', anagrafica_referenti: 'referente', anagrafica_alias: 'alias' };

// Nome del servizio: link alla lista della sezione solo se l'utente ha il permesso, altrimenti solo testo
const nomeServizio = (utente, sezione, percorso, etichetta) => (puoAccedere(utente, sezione) ? <Link to={percorso}>{etichetta}</Link> : etichetta);

// Scheda di un'azienda: dati comuni, referenti, alias, servizi collegati, storico delle modifiche.
export default function SchedaAnagrafica() {
  const { id } = useParams();
  const navigate = useNavigate();
  const puoScrivere = usePuoScrivere('anagrafiche');
  const { utente } = useAuth();
  const [az, setAz] = useState(null);
  const [errore, setErrore] = useState('');
  const [nonTrovata, setNonTrovata] = useState(false);
  const [messaggio, setMessaggio] = useState('');
  const [modifica, setModifica] = useState(false);
  const [referente, setReferente] = useState(null);   // null | 'nuovo' | riga
  const [nuovoAlias, setNuovoAlias] = useState('');
  const [bloccoElimina, setBloccoElimina] = useState(null);

  // L'azienda e i suoi referenti hanno ciascuno la propria versione: elimina e togli alias mandano quella che l'operatore ha VISTO.
  // Gli aggiornamenti "silenziosi" (dopo aver aggiunto un alias o salvato un referente) cambiano ciò che si vede ma non la versione vista.
  const versioni = useVersioniViste();
  const vAz = a => ({ id: `az${a.id}`, version: a.version });
  const vRef = r => ({ id: `ref${r.id}`, version: r.version });
  const carica = useCallback(async ({ silenzioso = false } = {}) => {
    try {
      const res = await api.get(`/anagrafiche/${id}`);
      versioni.aggiorna([vAz(res.data), ...(res.data.referenti || []).map(vRef)], { silenzioso });
      setAz(res.data);
      setNonTrovata(false);
    } catch (err) {
      if (err.response?.status === 404) setNonTrovata(true);
      else setErrore(err.response?.data?.errore || 'Errore nel caricamento');
    }
  }, [id, versioni]);
  useEffect(() => { setAz(null); carica(); }, [carica]);

  const avvisa = t => { setMessaggio(t); setTimeout(() => setMessaggio(''), 4000); };
  const gestisci = (err, predefinito) => {
    const d = err.response?.data;
    if (err.response?.status === 409 && d?.codice === 'CONFLITTO_VERSIONE') setErrore(`${d.errore}: l'azienda è stata modificata da un altro operatore. Ricarica la scheda.`);
    else setErrore(d?.errore || predefinito);
  };

  const archivia = async (attiva) => {
    setErrore('');
    try {
      await api.put(`/anagrafiche/${id}`, { version: az.version, attiva });
      avvisa(attiva ? 'Azienda ripristinata dall\'archivio' : 'Azienda archiviata');
      setBloccoElimina(null);
      carica();
    } catch (err) { gestisci(err, 'Operazione non riuscita'); }
  };

  const elimina = async () => {
    if (!window.confirm('Spostare questa azienda nel Cestino?')) return;
    setErrore('');
    try {
      await api.delete(urlEliminazione(`/anagrafiche/${id}`, versioni.versioneVista(vAz(az))));
      window.dispatchEvent(new Event('cestino-aggiornato'));
      navigate('/anagrafiche');
    } catch (err) {
      if (err.response?.data?.codice === 'COLLEGATA_A_SERVIZI') setBloccoElimina(err.response.data.errore);
      else { gestisci(err, 'Eliminazione non riuscita'); if (eConflitto(err)) { avvisaConflitto(err, `Non eliminata: ${err.response.data.errore}. Ricarica la scheda e controlla prima di eliminare.`); carica(); } }
    }
  };

  const aggiungiAlias = async (e) => {
    e.preventDefault();
    if (!nuovoAlias.trim()) return;
    setErrore('');
    try { await api.post(`/anagrafiche/${id}/alias`, { nome: nuovoAlias }); setNuovoAlias(''); carica({ silenzioso: true }); } catch (err) { gestisci(err, 'Alias non aggiunto'); }
  };
  const togliAlias = async (a) => {
    setErrore('');
    try { await api.delete(urlEliminazione(`/anagrafiche/${id}/alias/${a.id}`, versioni.versioneVista(vAz(az)))); carica({ silenzioso: true }); } catch (err) { gestisci(err, 'Alias non tolto'); if (eConflitto(err)) { avvisaConflitto(err, `Alias non tolto: ${err.response.data.errore}. Ricarica la scheda e controlla prima di continuare.`); carica(); } }
  };
  const eliminaReferente = async (r) => {
    if (!window.confirm(`Eliminare il referente «${r.nome}»?`)) return;
    setErrore('');
    try { await api.delete(urlEliminazione(`/anagrafiche/${id}/referenti/${r.id}`, versioni.versioneVista(vRef(r)))); carica({ silenzioso: true }); } catch (err) { gestisci(err, 'Referente non eliminato'); if (eConflitto(err)) { avvisaConflitto(err, `Referente non eliminato: ${err.response.data.errore}. Ricarica la scheda e controlla prima di continuare.`); carica(); } }
  };

  if (nonTrovata) {
    return <Layout><div style={stili.errore}>Azienda non trovata (forse è stata eliminata). <Link to="/anagrafiche">Torna all'elenco</Link></div></Layout>;
  }
  if (!az) return <Layout>{errore ? <div style={stili.errore}>{errore}</div> : <p style={{ color: '#777' }}>Caricamento…</p>}</Layout>;

  const riga = (etichetta, valore) => (
    <div style={st.voce}><dt style={st.etichetta}>{etichetta}</dt><dd style={st.valore}>{valore || <span style={{ color: '#bbb' }}>—</span>}</dd></div>
  );
  const sede = [az.indirizzo, [az.cap, az.comune].filter(Boolean).join(' '), az.provincia ? `(${az.provincia})` : ''].filter(Boolean).join(', ');
  const nServizi = az.servizi.totale;
  const codiciParcellazione = az.servizi.parcellazione_scadenziario.flatMap(c => (c.codici_fatturanti || []).map(f => ({ ...f, cliente: c.id })));

  return (
    <Layout>
      <p style={{ margin: '0 0 0.6rem' }}><Link to="/anagrafiche" style={{ color: '#555', fontSize: '0.88rem' }}>← Anagrafiche</Link></p>
      <div style={stili.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>{az.ragione_sociale}</h2>
          {versioni.cambiata(vAz(az)) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(vAz(az))} />}
          {az.stato === 'da_completare' && <span style={badge.daCompletare}>Da completare</span>}
          {!az.attiva && <span style={badge.archiviata}>Archiviata</span>}
        </div>
        {puoScrivere && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button onClick={() => setModifica(true)} style={stili.btn}>Modifica</button>
            <button onClick={() => archivia(!az.attiva)} style={stili.btnSecondary}>{az.attiva ? 'Archivia' : 'Ripristina dall\'archivio'}</button>
            <button onClick={elimina} style={{ ...stili.btnSecondary, color: '#b91c1c', borderColor: '#fecaca' }}>Elimina</button>
          </div>
        )}
      </div>

      {errore && <div style={stili.errore} role="alert">{errore} <button onClick={() => { setErrore(''); carica(); }} style={stili.btnSmall}>Ricarica</button></div>}
      {messaggio && <div style={st.ok} role="status">{messaggio}</div>}
      {bloccoElimina && (
        <div role="alert" style={{ ...stili.errore, border: '1px solid #fecaca' }}>
          {bloccoElimina}{puoScrivere && az.attiva && <> <button onClick={() => archivia(false)} style={stili.btn}>Archivia l'azienda</button></>}
        </div>
      )}
      {az.avvisi.length > 0 && (
        <ul aria-label="Avvisi" style={{ margin: '0 0 1rem', paddingLeft: '1.2rem', color: '#9a3412', fontSize: '0.86rem' }}>
          {az.avvisi.map(a => <li key={a.codice + a.campo}>{a.messaggio}</li>)}
        </ul>
      )}

      <section style={st.scheda} aria-label="Dati">
        <h3 style={st.titolo}>Dati dell'azienda</h3>
        <dl style={st.griglia}>
          {riga('Tipo di soggetto', TIPI_SOGGETTO[az.tipo_soggetto])}
          {riga('Codice fiscale', az.codice_fiscale)}
          {az.tipo_soggetto !== 'persona_fisica' && riga('Partita IVA', az.partita_iva)}
          {riga('Codice azienda (paghe e contabilità)', az.codice_azienda)}
          {riga('Codice ATECO', az.codice_ateco)}
          {riga('Sede', sede)}
          {riga('PEC', az.pec)}
          {riga('Email', az.email)}
          {riga('Telefono', az.telefono)}
          {riga('Note', az.note)}
        </dl>
        <p style={st.piccolo}>Creata {quando(az.created_at)}{az.creata_da_nome ? ` da ${az.creata_da_nome}` : ''}. Ultima modifica {quando(az.updated_at)}{az.modificata_da_nome ? ` da ${az.modificata_da_nome}` : ''}.</p>
      </section>

      <section style={st.scheda} aria-label="Referenti">
        <div style={st.testata}>
          <h3 style={st.titolo}>Referenti</h3>
          {puoScrivere && <button onClick={() => setReferente('nuovo')} style={stili.btnSmall}>+ Aggiungi referente</button>}
        </div>
        {az.referenti.length === 0 ? <p style={st.vuoto}>Nessun referente.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...stili.table, boxShadow: 'none' }}>
              <thead><tr>{['Nome', 'Ruolo', 'Email', 'Telefono', 'Note'].map(t => <th key={t} style={stili.th}>{t}</th>)}{puoScrivere && <th style={stili.th} />}</tr></thead>
              <tbody>
                {az.referenti.map(r => (
                  <tr key={r.id} style={stili.tr}>
                    <td style={stili.td}>{r.nome}{versioni.cambiata(vRef(r)) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(vRef(r))} />}</td><td style={stili.td}>{r.ruolo}</td><td style={stili.td}>{r.email}</td><td style={stili.td}>{r.telefono}</td><td style={stili.td}>{r.note}</td>
                    {puoScrivere && <td style={{ ...stili.td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => setReferente(r)} style={stili.btnSmall}>Modifica</button>
                      <button onClick={() => eliminaReferente(r)} style={stili.btnSmallDanger}>Elimina</button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={st.scheda} aria-label="Alias">
        <h3 style={st.titolo}>Alias (altri nomi dell'azienda, ricercabili)</h3>
        {az.alias.length === 0 ? <p style={st.vuoto}>Nessun alias.</p> : (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            {az.alias.map(a => (
              <span key={a.id} style={st.chip}>{a.nome}{puoScrivere && <button onClick={() => togliAlias(a)} aria-label={`Togli l'alias ${a.nome}`} style={st.chipX}>×</button>}</span>
            ))}
          </div>
        )}
        {puoScrivere && (
          <form onSubmit={aggiungiAlias} style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <input aria-label="Nuovo alias" placeholder="Aggiungi un alias…" value={nuovoAlias} onChange={e => setNuovoAlias(e.target.value)} style={{ ...stili.input, flex: '1 1 220px', maxWidth: '360px' }} />
            <button type="submit" style={stili.btnSecondary} disabled={!nuovoAlias.trim()}>Aggiungi</button>
          </form>
        )}
      </section>

      <section style={st.scheda} aria-label="Servizi collegati">
        <h3 style={st.titolo}>Servizi collegati</h3>
        {nServizi === 0 ? <p style={st.vuoto}>Nessun servizio collegato.</p> : (
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem' }}>
            {az.servizi.intermittenti.map(x => (
              <li key={`i${x.id}`}>{nomeServizio(utente, 'intermittenti', '/intermittenti', 'Intermittenti')}{!x.attiva ? ' (non attiva)' : ''}: {x.account} account</li>
            ))}
            {az.servizi.parcellazione_scadenziario.map(x => (
              <li key={`s${x.id}`}>{nomeServizio(utente, 'scadenziario', '/scadenziario/ditte', 'Scadenziario')}{!x.attivo ? ' (non seguita)' : ''}: {x.pratiche} {x.pratiche === 1 ? 'pratica' : 'pratiche'}</li>
            ))}
            {az.servizi.parcellazione_scadenziario.map(x => (
              <li key={`p${x.id}`}>{nomeServizio(utente, 'competenze', '/competenze/clienti', 'Parcellazione')}{!x.attivo ? ' (non seguita)' : ''}: {x.competenze} {x.competenze === 1 ? 'competenza' : 'competenze'}</li>
            ))}
          </ul>
        )}
      </section>

      <section style={st.scheda} aria-label="Codici azienda parcellazione">
        <h3 style={st.titolo}>Codici azienda parcellazione (per fatturante)</h3>
        {codiciParcellazione.length === 0 ? <p style={st.vuoto}>Nessun codice parcellazione.</p> : (
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9rem' }}>
            {codiciParcellazione.map(c => <li key={`${c.cliente}-${c.fatturante_id}`}>{c.fatturante}: <strong>{c.codice}</strong></li>)}
          </ul>
        )}
        <p style={{ ...st.vuoto, margin: '0.6rem 0 0', fontSize: '0.82rem' }}>Sono diversi dal codice azienda (paghe e contabilità) dei dati comuni. Si modificano in Parcellazione → Aziende.</p>
      </section>

      <section style={st.scheda} aria-label="Storico">
        <h3 style={st.titolo}>Storico delle modifiche</h3>
        {az.storico.length === 0 ? <p style={st.vuoto}>Nessuna voce.</p> : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.86rem' }}>
            {az.storico.map(s => (
              <li key={s.id} style={{ padding: '0.35rem 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#777' }}>{quando(s.at)}</span> · {s.operatore || 'sistema'} · {AZIONI[s.azione] || s.azione} {ENTITA[s.entita] || ''}
                {s.campi.length > 0 && <span style={{ color: '#555' }}>: {s.campi.map(c => ETICHETTE_CAMPI[c] || c).join(', ')}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {modifica && <FormAnagrafica anagrafica={az} onChiudi={() => setModifica(false)} onRicarica={() => { setModifica(false); carica(); }} onSalvato={() => { setModifica(false); avvisa('Modifiche salvate'); carica(); }} />}
      {referente && <FormReferente anagraficaId={az.id} referente={referente === 'nuovo' ? null : referente} onChiudi={() => setReferente(null)} onSalvato={() => { setReferente(null); carica({ silenzioso: true }); }} />}
    </Layout>
  );
}

function FormReferente({ anagraficaId, referente, onChiudi, onSalvato }) {
  const [form, setForm] = useState({ nome: referente?.nome || '', ruolo: referente?.ruolo || '', email: referente?.email || '', telefono: referente?.telefono || '', note: referente?.note || '' });
  const [errore, setErrore] = useState('');
  const campo = (nome, etichetta, tipo = 'text') => (
    <div style={stili.campo}>
      <label style={stili.label} htmlFor={`ref-${nome}`}>{etichetta}</label>
      <input id={`ref-${nome}`} type={tipo} value={form[nome]} onChange={e => setForm(f => ({ ...f, [nome]: e.target.value }))} style={stili.input} />
    </div>
  );
  const salva = async (e) => {
    e.preventDefault();
    setErrore('');
    try {
      if (referente) await api.put(`/anagrafiche/${anagraficaId}/referenti/${referente.id}`, { ...form, version: referente.version });
      else await api.post(`/anagrafiche/${anagraficaId}/referenti`, form);
      onSalvato();
    } catch (err) {
      const d = err.response?.data;
      setErrore(err.response?.status === 409 && d?.codice === 'CONFLITTO_VERSIONE' ? `${d.errore}: il referente è stato modificato da un altro operatore. Chiudi e ricarica la scheda.` : d?.errore || 'Salvataggio non riuscito');
    }
  };
  return (
    <div style={stili.overlay} role="dialog" aria-label={referente ? 'Modifica referente' : 'Nuovo referente'}>
      <div style={{ ...stili.modal, maxWidth: '480px' }}>
        <h3 style={{ marginTop: 0 }}>{referente ? 'Modifica referente' : 'Nuovo referente'}</h3>
        <form onSubmit={salva}>
          {errore && <div style={stili.errore}>{errore}</div>}
          {campo('nome', 'Nome')}
          {campo('ruolo', 'Ruolo')}
          {campo('email', 'Email', 'email')}
          {campo('telefono', 'Telefono')}
          {campo('note', 'Note')}
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn} disabled={!form.nome.trim()}>Salva</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const st = {
  scheda: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1rem 1.2rem', marginBottom: '1rem' },
  titolo: { margin: '0 0 0.7rem', fontSize: '1rem' },
  testata: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' },
  griglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '0.7rem 1.2rem', margin: 0 },
  voce: { minWidth: 0 },
  etichetta: { fontSize: '0.74rem', color: '#777', fontWeight: 600 },
  valore: { margin: '0.1rem 0 0', fontSize: '0.92rem', overflowWrap: 'anywhere' },
  piccolo: { fontSize: '0.78rem', color: '#888', margin: '0.9rem 0 0' },
  vuoto: { color: '#999', fontSize: '0.88rem', margin: 0 },
  ok: { background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#F7F7EA', border: '1px solid #D3D678', borderRadius: '999px', padding: '0.15rem 0.3rem 0.15rem 0.7rem', fontSize: '0.85rem' },
  chipX: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', lineHeight: 1, color: '#666' },
};
